import { SerializableServiceInfo } from '@kemu-io/hs-types';
import { useCallback } from 'react';
import { useHubContext } from '@common/context/HubContext/HubContext';

// Official service names
export const DefaultServices = {
  ServiceInstaller: {
    name: 'kemu.io.serviceInstaller',
    actions: {
      installService: 'installService',
      uninstallService: 'uninstallService',
      /** Returns a list of services that are being installed */
      getPendingInstallations: 'getPendingInstallations',
    }
  },
  ProjectCreator: {
    name: 'kemu.io.projectCreator',
    actions: {
      generateProject: 'generateProject',
    }
  }
};

/**
 * Helper function to find the project creator service in the list of active services.
 * @param services a list of active services
 * @returns a service info object if the project creator service is found, otherwise null.
 */
export const findService = (services: SerializableServiceInfo[], targetService: string): SerializableServiceInfo | null => {
  const projectCreator  = services.find((service) => service.name === targetService && service.internal);
  return projectCreator || null;
};

/**
 * Hook to get one of the available default services from the connected hub.
 * @returns a tuple, where the first element is a function that can be used to get the default service, 
 * and the second element is a boolean indicating if the default services are ready.
 */
const useHubDefaultService = (): [
  (serviceName: string) => SerializableServiceInfo | null,
  boolean,
] => {
  const { services, isReady } = useHubContext();

  const getDefaultService = useCallback((serviceName: string) => {
    if (!isReady) {
      return null;
    }

    return findService(services.available, serviceName);
  }, [services, isReady]);

  return [
    getDefaultService,
    isReady,
  ];
};

export default useHubDefaultService;
